import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import useBackendApi from '../../hooks/useBackendApi';
import useLocalSuccessErrMsg from '../../hooks/useLocalSuccessErrMsg';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import useSteps from '../../hooks/useSteps';
import ErrorModal from '../../lib/ErrorModal/ErrorModal';
import {
  clearUserDataFromStorage,
  getUserDataFromStorage,
  isTokenExpired,
  replaceUrlParamsWithValue,
  setUserDataToStorage,
} from '../../utils';
import FormWizard from '../FormWizard/FormWizard';
import SwipeTransition from '../SwipeTransition/SwipeTransition';
import WizardFinishStep from '../WizardFinishStep/WizardFinishStep';
import CodeInput from './components/CodeInput/CodeInput';
import DateInput from './components/DateInput/DateInput';
import MobileInput from './components/MobileInput/MobileInput';
import OfferedOptions from './components/OfferedOptions/OfferedOptions';
import RescheduleInProgress from './components/RescheduleInProgress/RescheduleInProgress';
import RescheduleOptions from './components/RescheduleOptions/RescheduleOptions';
import TimeConstraintInput from './components/TimeConstraintInput/TimeConstraintInput';
import TimeInput from './components/TimeInput/TimeInput';

interface IRescheduleBookingWizardProps {
  offerId?: string;
  open?: boolean;
  rescheduleInProgress?: boolean;
  rescheduleOffered?: boolean;
  drawerView?: boolean;
  onClose?: (e?: any) => void;
  onFinish?: (e?: any, action?: string) => void;
  onAcceptOffer?: (e?: any) => void;
  onCancelBooking?: (e?: any) => void;
}

const RescheduleBookingWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<IRescheduleBookingWizardProps>>> = ({
  offerId,
  open,
  drawerView,
  onClose,
  onFinish,
  rescheduleInProgress,
  rescheduleOffered,
  onCancelBooking,
}) => {
  const { get, post, del } = useBackendApi();

  const {
    step,
    steps,
    setSteps,
    setStep,
    isLastStep,
    isFirstStep,
    index,
    next,
    prev,
  } = useSteps([] as string[]);

  const [input, handleChange, setInput] = useStateWithInputEventHandler({
    mobile: '',
    code: '',
    changeDate: false,
    changeTime: false,
    date: '',
    timeConstraint: '',
    time: '',
    reason: '',
    offeredOption: '',
  });
  const {
    mobile,
    code,
    changeDate,
    changeTime,
    date,
    timeConstraint,
    time,
    reason,
    offeredOption,
  } = input;

  const {
    loading,
    setLoading,
    err,
    setErr,
    clearErr,
    success,
    setSuccess,
    clearSuccess,
  } = useLocalSuccessErrMsg();

  const [response, setResponse] = React.useState<any>();

  React.useEffect(() => {
    if (open) {
      const token = getUserDataFromStorage().token;
      let input: any = {
        mobile: '',
        code: '',
        changeDate: false,
        changeTime: false,
        date: '',
        timeConstraint: '',
        time: '',
        reason: '',
        offeredOption: '',
      };
      let steps = ['dateOrTime', 'date', 'timeConstraint', 'time'];
      let extraSteps: string[] = [];
      if (isTokenExpired(token)) {
        extraSteps = ['mobile', 'code'];
      }
      if (rescheduleInProgress) {
        steps = ['rescheduleInProgress'];
      }
      if (rescheduleOffered) {
        steps = ['offeredOptions'];
      }
      steps = [...extraSteps, ...steps, 'finish'];
      setSteps(steps);
      setInput(input);
      return () => {
        setSteps([]);
        setInput(input);
      };
    }
  }, [open, rescheduleInProgress, rescheduleInProgress, rescheduleOffered]);

  React.useEffect(() => {
    if (step === 'dateOrTime' && (changeDate || changeTime)) {
      (!changeDate && setStep('timeConstraint')) || setStep('date');
    }
  }, [steps]);

  React.useEffect(() => {
    if (steps.length && isLastStep) {
      err && clearErr();
      success && clearSuccess();
      let callback: any = submitRescheduleRequest;
      if (rescheduleInProgress) {
        callback = cancelRequest;
      }
      if (rescheduleOffered) {
        callback = handleOfferedOptionSubmit;
      }
      callback();
    }
  }, [steps, isLastStep]);

  function handleBack() {
    if (isFirstStep) {
      if (rescheduleOffered && steps.includes('dateOrTime')) {
        return setSteps(['offeredOptions', 'finish']);
      }
      return onClose?.();
    }
    prev();
  }

  async function handleNext() {
    if (isLastStep) {
      onFinish?.(response);
      return onClose?.();
    }
    switch (step) {
      case 'mobile':
        return submitMobile();
      case 'code':
        return submitCode(code);
      case 'dateOrTime':
        if (!changeDate) {
          setSteps(['dateOrTime', 'timeConstraint', 'time', 'finish']);
        } else if (!changeTime) {
          setSteps(['dateOrTime', 'date', 'finish']);
        } else {
          setSteps(['dateOrTime', 'date', 'timeConstraint', 'time', 'finish']);
        }
        return;
      case 'offeredOptions':
        if (offeredOption === 'requestNew') {
          return setSteps([
            'dateOrTime',
            'date',
            'timeConstraint',
            'time',
            'finish',
          ]);
        }
        break;
    }
    next();
  }

  function handleClear() {
    switch (step) {
      case 'time':
        let field = `${timeConstraint}Time`;
        setInput({ ...input, [field]: '' });
        break;
      default:
        setInput({ ...input, [step]: '' });
    }
  }

  async function submitMobile() {
    setLoading(true);
    const url = replaceUrlParamsWithValue('/v1/users/mobileLogin/:mobile', {
      mobile,
    });
    await get(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setErr(err.message);
          setLoading(false);
        },
      }
    );
    setLoading(false);
    if (step === 'mobile') {
      next();
    }
  }

  async function submitCode(code) {
    setLoading(true);
    const url = replaceUrlParamsWithValue(
      '/v1/users/mobileLogin/:mobile/:code',
      {
        mobile,
        code,
      }
    );
    const res = await post(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setErr(err.message);
          setLoading(false);
        },
      }
    );
    setLoading(false);
    setUserDataToStorage({ token: res.token, pathRequested: '' });
    if (step === 'code') {
      next();
      setSteps(steps.slice(2));
    }
  }

  async function submitRescheduleRequest() {
    let data: any = {};
    if (changeDate) {
      data.eventDate = date;
    }
    if (changeTime) {
      const field = `${timeConstraint}Time`;
      data[field] = time;
      data.reason = reason;
    }
    const url = replaceUrlParamsWithValue(
      `/v1/offers/:offerId/reschedule/request`,
      { offerId }
    );
    setLoading(true);
    const res = await post(url, data, {
      loader: false,
      onError: (err) => {
        setErr(err.message);
        clearUserDataFromStorage();
        setLoading(false);
      },
    });
    setLoading(false);
    setSuccessMsg(res.data);
    setResponse?.(res.data);
  }

  async function cancelRequest() {
    const url = replaceUrlParamsWithValue(
      `/v1/offers/:offerId/reschedule/request`,
      { offerId }
    );
    setLoading(true);
    const res = await del(
      url,
      {},
      {
        loader: false,
        onError: (err) => {
          setErr(err.message);
          clearUserDataFromStorage();
          setLoading(false);
        },
      }
    );
    setLoading(false);
    setSuccessMsg(res.data, 'cancel');
    setResponse?.(res.data);
  }

  async function acceptReschedule() {
    setLoading(true);
    const url = replaceUrlParamsWithValue(
      `/v1/offers/:offerId/reschedule/acceptOffer`,
      { offerId }
    );
    const res = await post(
      url,
      {},
      {
        onError: (err) => {
          setErr(err.message);
          clearUserDataFromStorage();
          setLoading(false);
        },
        loader: false,
      }
    );
    setLoading(false);
    setSuccessMsg(res.data);
    setResponse?.(res.data);
  }

  function handleOfferedOptionSubmit() {
    switch (offeredOption) {
      case 'accept':
        return acceptReschedule?.();
      case 'requestNew':
        return submitRescheduleRequest();
      case 'keepOriginal':
        return cancelRequest();
      case 'cancelBooking':
        onCancelBooking?.();
        return onClose?.();
    }
  }

  function setSuccessMsg(data, action?) {
    let msg = 'Request Cancelled';
    if (action !== 'cancel') {
      switch (data?.offer?.status) {
        case 'rescheduleRequested':
          msg = 'Reschedule Requested';
          break;
        case 'booked':
          msg = 'Offer Accepted';
          break;
      }
    }
    setSuccess(msg);
  }

  const isFormValid = React.useMemo(() => {
    switch (step) {
      case 'mobile':
        return mobile && isValidPhoneNumber(mobile);
      case 'code':
        return code.length === 4;
      case 'dateOrTime':
        return !!(changeDate || changeTime);
      case 'date':
        return !!date;
      case 'timeConstraint':
        return !!timeConstraint;
      case 'time':
        return timeConstraint && time && reason;
      case 'offeredOptions':
        return !!offeredOption;
      default:
        return true;
    }
  }, [input, step]);

  const stepHeader = React.useMemo(() => {
    const stepsHeadingsMapping = {
      mobile: {
        title: `What's your mobile number?`,
        subTitle: 'Login',
      },
      code: {
        subTitle: 'Verification Code',
        title: `Enter code texted to your mobile`,
      },
      dateOrTime: {
        subTitle: 'Reschedule',
        title: 'What would you like to change?',
      },
      date: {
        subTitle: 'Date',
        title: 'When is your event?',
      },
      timeConstraint: {
        subTitle: 'Reschedule time',
        title: 'Time',
      },
      startTime: {
        subTitle: 'Start Time',
        title: 'What time would you like to start after?',
      },
      finishTime: {
        subTitle: 'Finish Time',
        title: 'What time would you like to be ready by',
      },
      rescheduleInProgress: {
        subTitle: 'Reschedule',
        title: 'In Progress',
      },
      offeredOptions: {
        subTitle: 'Reschedule',
        title: 'What would you like to do?',
      },
      finish: {
        subTitle: 'Reschedule',
        title: 'Finish',
      },
    };
    let field = step;
    if (step === 'time') {
      field = `${timeConstraint}Time`;
    }
    return stepsHeadingsMapping[field];
  }, [step, timeConstraint]);

  return (
    <FormWizard
      open={open}
      onClose={onClose}
      onBack={handleBack}
      title={stepHeader?.title}
      drawerView={drawerView}
      subTitle={stepHeader?.subTitle}
      onNext={handleNext}
      nextBtnDisabled={!isFormValid}
      nextBtnLabel={
        // (isLastStep && rescheduleOffered && 'Finish') ||
        (!isLastStep && rescheduleInProgress && 'Cancel Request') ||
        (isLastStep && 'Finish') ||
        'Next'
      }
      onClear={handleClear}
      loading={loading}
      hideClearBtn={isLastStep}
      hideBackBtn={isLastStep}
    >
      {!rescheduleInProgress && !rescheduleOffered && (
        <SwipeTransition activeStepIndex={index} duration="500">
          {steps.includes('mobile') && (
            <MobileInput value={mobile} onChange={handleChange} />
          )}
          {steps.includes('code') && (
            <CodeInput
              value={code}
              onChange={handleChange}
              onResendCode={submitMobile}
              onComplete={submitCode}
            />
          )}

          <RescheduleOptions
            changeDate={changeDate}
            changeTime={changeTime}
            onChange={handleChange}
          />

          {changeDate && <DateInput value={date} onChange={handleChange} />}
          {changeTime && (
            <TimeConstraintInput
              value={timeConstraint}
              onChange={handleChange}
            />
          )}
          {changeTime && (
            <TimeInput
              timeConstraint={timeConstraint}
              time={time}
              reason={reason}
              onChange={handleChange}
            />
          )}

          <WizardFinishStep
            loading={loading}
            success={success}
            err={err}
            extension={
              !!success && (
                <div className="mt-8">
                  We will email and text you if the stylist is able to
                  accommodate the new date/time reschedule request
                </div>
              )
            }
          />
        </SwipeTransition>
      )}

      {rescheduleInProgress && (
        <SwipeTransition activeStepIndex={index} duration="500">
          {steps.includes('mobile') && (
            <MobileInput value={mobile} onChange={handleChange} />
          )}
          {steps.includes('code') && (
            <CodeInput
              value={code}
              onChange={handleChange}
              onResendCode={submitMobile}
              onComplete={submitCode}
            />
          )}
          <RescheduleInProgress />
          <WizardFinishStep loading={loading} success={success} err={err} />
        </SwipeTransition>
      )}

      {rescheduleOffered && (
        <SwipeTransition activeStepIndex={index} duration="500">
          {steps.includes('mobile') && (
            <MobileInput value={mobile} onChange={handleChange} />
          )}
          {steps.includes('code') && (
            <CodeInput
              value={code}
              onChange={handleChange}
              onResendCode={submitMobile}
              onComplete={submitCode}
            />
          )}
          {steps.includes('offeredOptions') && (
            <OfferedOptions value={offeredOption} onChange={handleChange} />
          )}
          {steps.includes('dateOrTime') && (
            <RescheduleOptions
              changeDate={changeDate}
              changeTime={changeTime}
              onChange={handleChange}
            />
          )}

          {steps.includes('date') && changeDate && (
            <DateInput value={date} onChange={handleChange} />
          )}
          {steps.includes('timeConstraint') && changeTime && (
            <TimeConstraintInput
              value={timeConstraint}
              onChange={handleChange}
            />
          )}
          {steps.includes('time') && changeTime && (
            <TimeInput
              timeConstraint={timeConstraint}
              time={time}
              reason={reason}
              onChange={handleChange}
            />
          )}
          <WizardFinishStep
            loading={loading}
            success={success}
            err={err}
            extension={
              !!success &&
              offeredOption === 'requestNew' && (
                <div className="mt-8">
                  We will email and text you if the stylist is able to
                  accommodate the new date/time reschedule request
                </div>
              )
            }
          />
        </SwipeTransition>
      )}

      <div className="text-gray-700">
        <ErrorModal open={!!err} onClose={clearErr} message={err} size="lg" />
      </div>
    </FormWizard>
  );
};
export default RescheduleBookingWizard;
